'use client';

import { c as _c } from "react/compiler-runtime";
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef } from 'react';
import Select, { components as RSComponents, OptionProps, MenuListProps, GroupBase, SingleValue, StylesConfig, MultiValue, DropdownIndicatorProps, ClearIndicatorProps, Props as RSProps } from 'react-select';
import { useTranslation } from 'react-i18next';
import { Scrollbars } from 'rc-scrollbars';
import { LFBounceLoader } from '@components/spinner/Spinner';
import { AsyncPaginate, LoadOptions, wrapMenuList } from 'react-select-async-paginate';
import { ControlComponent, DefaultSingleValue, IBaseOption, Menu, generateDefaultDropdownStyles } from './utils';
import { useLFSearchParams } from '@services/useLFSearchParams';
import { FaUndoAlt } from '@react-icons/all-files/fa/FaUndoAlt';
import { isPresent } from '@farmai-shared/LFUtils';
const ClearIndicator = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.innerProps) {
    t0 = {
      ...props.innerProps,
      "data-ignore-outside-click": true
    };
    $[0] = props.innerProps;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = t0 as any;
  let t2;
  if ($[2] !== props || $[3] !== t1) {
    t2 = <RSComponents.ClearIndicator {...props} innerProps={t1} />;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export interface IBaseSelect<T, IsMulti extends boolean = false> extends Omit<RSProps<T, IsMulti>, 'defaultValue' | 'components' | 'styles'> {
  id?: string;
  customComponents?: Partial<RSProps<T, IsMulti, GroupBase<T>>['components']>;
  useAsync?: boolean;
  defaultValue?: T;
  onReset?: () => void;
  loadOptions?: LoadOptions<T, GroupBase<T>, {
    page: number;
  }>;
  customDropdownStyles?: (defaultStyles: StylesConfig<T, IsMulti, GroupBase<T>>) => StylesConfig<T, IsMulti, GroupBase<T>>;
  disabled?: boolean;
  testId?: string;
  withDefault?: boolean;
  extendedOnChange?: (value?: IsMulti extends true ? MultiValue<T> : SingleValue<T>) => void;
  queryParamKey?: string;
  cacheUniqs?: (string | number | boolean | object)[];
}
export const ResetIndicator = props => {
  const $ = _c(14);
  let innerProps;
  let restProps;
  if ($[0] !== props) {
    ({
      innerProps,
      ...restProps
    } = props);
    $[0] = props;
    $[1] = innerProps;
    $[2] = restProps;
  } else {
    innerProps = $[1];
    restProps = $[2];
  }
  const onReset = props.selectProps.onReset;
  if (!isPresent(onReset)) {
    let t0;
    if ($[3] !== props) {
      t0 = <RSComponents.DropdownIndicator {...props} />;
      $[3] = props;
      $[4] = t0;
    } else {
      t0 = $[4];
    }
    return t0;
  }
  let t0;
  if ($[5] !== onReset) {
    t0 = e => {
      e.preventDefault();
      e.stopPropagation();
      onReset();
    };
    $[5] = onReset;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const handleReset = t0;
  let t1;
  if ($[7] !== handleReset || $[8] !== innerProps) {
    t1 = {
      ...innerProps,
      onMouseDownCapture: handleReset,
      onTouchStartCapture: handleReset
    };
    $[7] = handleReset;
    $[8] = innerProps;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <button type="button" className="cursor-pointer"><FaUndoAlt /></button>;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== restProps || $[12] !== t1) {
    t3 = <RSComponents.DropdownIndicator {...restProps} innerProps={t1}>{t2}</RSComponents.DropdownIndicator>;
    $[11] = restProps;
    $[12] = t1;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};

// without passing default option/singlevalue components shows [Object Object]
const DefaultOption = observer(<T extends IBaseOption, IsMulti extends boolean = false>(props: OptionProps<T, IsMulti, GroupBase<T>>) => {
  const {
    t
  } = useTranslation();
  return <RSComponents.Option {...props}>
        <p>{t(props.data?.label, {
        defaultValue: props.data?.label
      })}</p>
      </RSComponents.Option>;
});
const AsyncMenuList = wrapMenuList(observer(<T extends IBaseOption, IsMulti extends boolean = false>(props: MenuListProps<T, IsMulti, GroupBase<T>>) => {
  const intermediateRef = useRef<Scrollbars | null>(null);
  useEffect(() => {
    if (intermediateRef.current?.view && typeof props.innerRef === 'function') {
      props.innerRef(intermediateRef.current.view as HTMLDivElement);
    }
  }, [props.innerRef, intermediateRef]);
  return <RSComponents.MenuList {...props}>
          <Scrollbars data-testid="dropdown-menulist" className="custom-scrollbar" autoHeight ref={intermediateRef}>
            {props.children}
          </Scrollbars>
        </RSComponents.MenuList>;
}));
const MenuList = observer(<T extends IBaseOption, IsMulti extends boolean = false>(props: MenuListProps<T, IsMulti, GroupBase<T>>) => <Scrollbars data-testid="dropdown-menulist" className="custom-scrollbar" autoHide autoHeight>
      {props.children}
    </Scrollbars>);
export const LFBaseSelect = observer(<T extends IBaseOption, IsMultiFlag extends boolean = false>({
  id,
  label,
  options = [],
  onMenuOpen,
  disabled,
  customDropdownStyles,
  useAsync = false,
  required,
  testId,
  loadOptions = async () => ({
    options: [],
    hasMore: false,
    additional: {
      page: 1
    }
  }),
  // Default loadOptions function
  withDefault = true,
  value,
  onChange,
  extendedOnChange,
  formatGroupLabel,
  isLoading,
  queryParamKey,
  defaultValue,
  onMenuClose,
  textTransform,
  isMulti = false as IsMultiFlag,
  customComponents,
  closeMenuOnSelect = true,
  hideSelectedOptions = false,
  placeholder,
  isSearchable = true,
  cacheUniqs,
  onReset
}: IBaseSelect<T, IsMultiFlag>) => {
  const defaultStyles = generateDefaultDropdownStyles<T, IsMultiFlag>();
  const {
    setParams
  } = useLFSearchParams();
  const initialValue = useMemo(() => withDefault ? defaultValue || {
    label: 'All',
    value: ''
  } as T : undefined, [defaultValue, withDefault]);
  const getOptionValue = (option: T) => String(option.value ?? '');
  const replaceRoute = (option: SingleValue<T> | MultiValue<T>) => {
    if (!queryParamKey || option == null) return;
    const selections = Array.isArray(option) ? option : [option];
    setParams(selections.map(o => ({
      key: queryParamKey,
      value: o.value
    })));
  };
  const finalStyles = customDropdownStyles ? customDropdownStyles(defaultStyles) : defaultStyles;
  const defaultComponents: RSProps<T, IsMultiFlag, GroupBase<T>>['components'] = {
    Control: ControlComponent,
    DropdownIndicator: isLoading ? () => <LFBounceLoader size={16} isLoading /> : ResetIndicator,
    IndicatorSeparator: () => null,
    LoadingIndicator: () => null,
    MenuList: useAsync ? AsyncMenuList : MenuList as any,
    Menu: Menu,
    Option: DefaultOption,
    SingleValue: DefaultSingleValue,
    ClearIndicator
  };
  const mergedComponents = {
    ...defaultComponents,
    ...customComponents
  };
  return <div data-testid={testId} className="h-full w-full">
        {useAsync ? <AsyncPaginate<T, GroupBase<T>, {
      page: number;
    }, IsMultiFlag> id={id} name={label} isMulti={isMulti as IsMultiFlag} value={value} isRequired={required} defaultValue={initialValue} getOptionValue={getOptionValue} loadOptions={loadOptions} additional={{
      page: 1
    }} isDisabled={disabled || isLoading} label={label} formatGroupLabel={formatGroupLabel} styles={finalStyles} textTransform={textTransform} loadingMessage={() => ''} // null removes loading message, blank keeps the box on initial press and shows our default loading indicator
    onChange={(newVal, meta) => {
      extendedOnChange && extendedOnChange(newVal);
      replaceRoute(newVal);
      onChange && onChange(newVal, meta);
    }} components={mergedComponents} onMenuClose={onMenuClose} closeMenuOnSelect={closeMenuOnSelect} hideSelectedOptions={hideSelectedOptions} placeholder={placeholder} isSearchable={isSearchable} cacheUniqs={cacheUniqs} onReset={onReset} /> : <Select<T, IsMultiFlag, GroupBase<T>> id={id} name={label} onReset={onReset} isMulti={isMulti as IsMultiFlag} value={value} label={label} isRequired={required} getOptionValue={getOptionValue} isDisabled={disabled || isLoading} onMenuOpen={onMenuOpen} styles={finalStyles} textTransform={textTransform} options={options} onChange={(newVal, meta) => {
      extendedOnChange && extendedOnChange(newVal);
      replaceRoute(newVal);
      onChange && onChange(newVal, meta);
    }} defaultValue={initialValue} components={mergedComponents} closeMenuOnSelect={closeMenuOnSelect} placeholder={placeholder} isSearchable={isSearchable} />}
      </div>;
});